import { useCallback } from 'react'
import { NotificationEventItem } from 'shared'
import { notificationHandler } from 'hooks/notificationHandler'

/**
 * returns a callback to help get the data for a specific event
 *
 * @param options {@link UseRecipientsFnOptions}
 * @see {@link UseRecipientsFn}
 */
export function useRecipientsFn<
  ReturnType,
  DataType,
  Event extends NotificationEventItem = NotificationEventItem
>({
  data,
  fallback,
  getter,
  error,
}: UseRecipientsFnOptions<DataType, ReturnType>) {
  return useCallback<UseRecipientsFn<ReturnType, Event>>(
    ({ id, name }) => {
      if (!data) return fallback
      const notification = getter(id, data)
      if (!notification) {
        if (error)
          notificationHandler({
            title: `${name} didn't get set up properly for this Project in the database`,
            alternateType: 'warning',
            duration: 'infinite',
            details: 'Attempting to configure this event may cause more errors',
          })
        return fallback
      }
      return notification
    },
    [data, error, fallback, getter]
  )
}

export type UseRecipientsFn<
  ReturnType,
  Event extends NotificationEventItem = NotificationEventItem
> = (event: Pick<Event, 'id' | 'name'>) => ReturnType

interface UseRecipientsFnOptions<T, U> {
  data?: T
  /**
   * a callback to get recipients out of {@link data}
   */
  getter: (id: string, data: T) => U | undefined
  /**
   * a value to return if data isn't defined yet
   *
   */
  fallback: U
  /**
   * should an error notification throw up if data is defined and the
   * value of {@link getter} is undefined?
   */
  error?: true
}
